import React, { useEffect, useRef, useState } from "react";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import * as am4themes_animated from "@amcharts/amcharts4/themes/animated";

am4core.useTheme(am4themes_animated.default);

const ChatbotCharts = ({ data, chartsData, handleChartTypeChange, downloadChat }) => {
  const chartRef = useRef(null); // Reference to hold the chart instance
  const chartId = useRef(`chartDiv-${Math.random().toString(36)}`);

  const renderChart = () => {
    // Dispose the previous chart instance before creating a new one
    if (chartRef.current) {
      chartRef.current.dispose();
      chartRef.current = null; // Ensure ref is reset
    }

    let chart;
    const yAxisUnit = chartsData[0]?.yAxisUnit || '';
    const legendName = chartsData[0]?.legendName || 'Value';

    // Create the chart based on selected type
    if (data.chartType === "bar") {
      chart = am4core.create(chartId.current, am4charts.XYChart);
      chart.data = chartsData;
      // Configure chart for bar type
      var categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
      categoryAxis.dataFields.category = "category";
      categoryAxis.renderer.labels.template.fontSize = 12;
      categoryAxis.renderer.grid.template.location = 0;
      categoryAxis.renderer.minGridDistance = 30;
      categoryAxis.renderer.labels.template.rotation = -60; // Rotate labels by 45 degrees
      categoryAxis.renderer.labels.template.horizontalCenter = "right"; // Align labels to the right
      categoryAxis.renderer.labels.template.verticalCenter = "middle"; // Align labels to the middle
      categoryAxis.renderer.labels.template.wrap = true; // Enable word wrap for long labels
      var valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
      valueAxis.min = 0;
      valueAxis.title.text = yAxisUnit;
      // valueAxis.strictMinMax = true;
      var series = chart.series.push(new am4charts.ColumnSeries());
      series.dataFields.valueY = "value";
      series.dataFields.categoryX = "category";
      series.name = legendName;
      series.columns.template.tooltipText = "{categoryX}: [bold]{valueY}[/] " + yAxisUnit;
      series.columns.template.fill = am4core.color("#00555C"); // Set bar color
      series.columns.template.stroke = am4core.color("#00555C"); // Set bar border color
    } else if (data.chartType === "line") {
      chart = am4core.create(chartId.current, am4charts.XYChart);
      chart.data = chartsData;
      var categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
      categoryAxis.dataFields.category = "category";
      categoryAxis.renderer.labels.template.fontSize = 12;
      categoryAxis.renderer.grid.template.location = 0;
      categoryAxis.renderer.minGridDistance = 30;
      categoryAxis.renderer.labels.template.rotation = -60; // Rotate labels by 45 degrees
      categoryAxis.renderer.labels.template.horizontalCenter = "right"; // Align labels to the right
      categoryAxis.renderer.labels.template.verticalCenter = "middle"; // Align labels to the middle
      categoryAxis.renderer.labels.template.wrap = true; // Enable word wrap for long labels
      var valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
      valueAxis.title.text = yAxisUnit;

      // Create series
      var series = chart.series.push(new am4charts.LineSeries());
      series.dataFields.valueY = "value";
      series.dataFields.categoryX = "category";
      series.name = legendName;
      series.stroke = am4core.color("#00555C"); // Set line color
      series.strokeWidth = 2; // Optional: adjust line thickness

      // Configure tooltips
      series.tooltip.pointerOrientation = "vertical";
      series.tooltip.getFillFromObject = false;
      series.tooltip.background.fill = am4core.color("#111");
      series.tooltipText = "{categoryX}: [bold]{valueY}[/] " + yAxisUnit;

      // Enable cursor
      chart.cursor = new am4charts.XYCursor();
      chart.cursor.snapToSeries = series;
    } else if (data.chartType === "pie") {
      chart = am4core.create(chartId.current, am4charts.PieChart);
      chart.data = chartsData;

      const pieSeries = chart.series.push(new am4charts.PieSeries());
      pieSeries.dataFields.value = "value";
      pieSeries.dataFields.category = "category";
      pieSeries.slices.template.tooltipText = "{category}: [bold]{value}[/] " + yAxisUnit;
      pieSeries.colors.step = 2;
      pieSeries.name = legendName;

      // Adjust font size for labels
      pieSeries.labels.template.fontSize = 10; // Set to desired smaller font size
      //pieSeries.labels.template.wrap = true; // Enable word wrap if needed
      pieSeries.labels.template.maxWidth = 100; // Optional: limit the width of the label text
    }

    chart.legend = new am4charts.Legend();
    chart.legend.labels.template.fontSize = 12; // Smaller font size for legend
    chart.legend.markers.template.width = 10; // Smaller marker size
    chart.legend.markers.template.height = 10;
    chart.legend.position = "bottom";
    chartRef.current = chart; // Store chart instance
  };

  useEffect(() => {
    renderChart();

    // Cleanup chart when component unmounts
    return () => {
      if (chartRef.current) {
        chartRef.current.dispose();
        chartRef.current = null; // Clear the reference to avoid memory leaks
      }
    };
  }, [data, chartsData]);

  return (
    <div>
      {!downloadChat && (
        <div>
          <label className="pr-4">
            <input
              type="radio"
              name={`chartType-${chartId.current}`} // Unique name for each chart instance
              value="bar"
              checked={data.chartType == "bar"}
              onChange={() => {
                handleChartTypeChange("bar", data.question_id);
              }}
            />
            <span className="pl-1">Bar Chart</span>
          </label>
          <label className="pr-4">
            <input
              type="radio"
              name={`chartType-${chartId.current}`} // Unique name for each chart instance
              value="line"
              checked={data.chartType == "line"}
              onChange={() => handleChartTypeChange("line", data.question_id)}
            />
            <span className="pl-1">Line Chart</span>
          </label>
          <label className="pr-4">
            <input
              type="radio"
              name={`chartType-${chartId.current}`} // Unique name for each chart instance
              value="pie"
              checked={data.chartType == "pie"}
              onChange={() => handleChartTypeChange("pie", data.question_id)}
            />
            <span className="pl-1">Pie Chart</span>
          </label>
        </div>
      )}
      <div className="paper">
        {/* Ensure the chart container ID changes dynamically */}
        {/* <div
                    id={`chartDiv-${chartType}`}
                    style={{ width: "100%", height: chartType === "pie" ? "500px" : "400px" }}
                ></div> */}
        <div
          id={chartId.current}
          style={{
            width: "100%",
            height: data.chartType === "pie" ? "500px" : "400px",
          }}
        ></div>
      </div>
    </div>
  );
};

export default ChatbotCharts;
